// hooks/useCashRunway.js
import { useQuery } from '@tanstack/react-query';
import { reactQueryFinanceDefaults } from './queryDefaults';
import apiService from '../../services/api';
import { ENDPOINTS } from '../../services/endpoints';

const useCashRunway = (selectedYear) => {
  const fetchCashRunway = async () => {
    const [cashResponse, expenseResponse] = await Promise.all([
      apiService.get(ENDPOINTS.FINANCE_CASH_ON_HAND),
      apiService.get(ENDPOINTS.FINANCE_AVERAGE_EXPENSES)
    ]);

    if (!cashResponse || typeof cashResponse !== 'object') {
      throw new Error('Invalid cash response');
    }

    if (!expenseResponse || !expenseResponse.average_expenses) {
      throw new Error('Invalid expense response');
    }

    const { total_cash, metadata } = cashResponse;

    return {
      currentCash: total_cash || 0,
      averageMonthlyExpense: expenseResponse.average_expenses || 0,
      asOfDate: metadata?.as_of_date || null,
      cashData: cashResponse,
      expenseData: expenseResponse,
      lastUpdated: new Date().toISOString(),
    };
  };

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    } = useQuery({
      queryKey: ['cashRunway', selectedYear],
      queryFn: fetchCashRunway,
      ...reactQueryFinanceDefaults,
    });

  return {
    // Cash runway data
    currentCash: data?.currentCash || 0,
    averageMonthlyExpense: data?.averageMonthlyExpense || 0,
    asOfDate: data?.asOfDate || null,
    cashData: data?.cashData || null,
    expenseData: data?.expenseData || null,
    lastUpdated: data?.lastUpdated || null,
    
    // Loading states
    loading: isLoading,
    refreshing: isFetching,
    
    // Error handling
    error: isError ? (error?.message || 'Failed to load cash runway data') : null,
    isError,
    
    // Status
    initialized: !!data,
    
    // Actions
    refetch,
  };
};

export { useCashRunway };
